import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Paper,
  Title,
  Button,
  Group,
  Stack,
  Text,
  FileInput,
  Card,
  Divider,
  Badge,
  Box,
  Flex,
  MultiSelect,
  NumberInput,
  Grid,
  Tooltip,
  Accordion,
  Loader,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";
import { IconUpload, IconSend, IconCheck } from "@tabler/icons-react";
import { useOptimizedEditor } from "../../../hooks/useOptimizedEditor";
import { RichTextEditorComponent } from "./RichTextEditor";
import axios from "axios";
import { PhoneInput } from "../../../components/PhoneInput/PhoneInput";
import { PhoneNumberItem } from "../../../components/PhoneInput/PhoneNumberItem";
import showNotification from "../../../utils/showNotification";

export function Messages({ form, loading }) {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [evolutionInstances, setEvolutionInstances] = useState({
    isLoading: false,
    data: [],
    error: "",
  });

  const editor = useOptimizedEditor();

  const handleAddPhone = useCallback(() => {
    if (newPhone && !phoneNumbers.includes(newPhone)) {
      setPhoneNumbers((prev) => [...prev, newPhone]);
      form.setFieldValue("phones", (prev) => [...prev, newPhone]);
      setNewPhone("");
      notifications.show({
        title: "Número agregado",
        message: "El número ha sido agregado a la lista",
        color: "blue",
      });
    }
  }, [newPhone, phoneNumbers]);

  const handleRemovePhone = useCallback((phone) => {
    const filteredPhones = phoneNumbers.filter((p) => p !== phone);
    setPhoneNumbers(filteredPhones);
    form.setFieldValue("phones", filteredPhones);
    notifications.show({
      title: "Número eliminado",
      message: "El número ha sido eliminado de la lista",
      color: "green",
    });
  }, []);

  const handleFileUpload = useCallback(
    (file) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          const numbers = content
            .split(/[\n,]/)
            .map((n) => n.trim())
            .filter((n) => n && !phoneNumbers.includes(n));
          setPhoneNumbers((prev) => [...prev, ...numbers]);
          form.setFieldValue("phones", (prev) => [
            ...prev,
            ...numbers,
          ]);
          notifications.show({
            title: "Archivo procesado",
            message: `${numbers.length} números han sido agregados`,
            color: "blue",
          });
        };
        reader.readAsText(file);
      }
    },
    [phoneNumbers]
  );

  const phoneNumbersList = useMemo(() => {
    return phoneNumbers.map((phone) => (
      <PhoneNumberItem
        key={phone}
        phone={phone}
        onRemove={handleRemovePhone}
      />
    ));
  }, [phoneNumbers, handleRemovePhone]);

  const handleConfirmMessage = () => {
    form.setFieldValue("message", null);
    form.setFieldValue("mediaData", null);
    const editorContent = editor.getHTML();
    if (!editor?.getText() && !editor?.getHTML().includes("img")) {
      notifications.show({
        title: "Error",
        message: "Por favor, escribe un mensaje o agrega una imagen",
        color: "red",
      });
      return;
    }
    const hasImage = editorContent.includes("img");
    let base64Image = "";
    let caption = "";

    if (hasImage) {
      // Extract base64 image and caption
      const imgMatch = editorContent.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch) {
        base64Image = imgMatch[1];
        // Get text content as WhatsApp formatted text
        caption = editor.getWhatsAppContent();
      }
    }

    const message_type = hasImage ? "media" : "text";
    form.setFieldValue("message_type", message_type);
    hasImage
      ? form.setFieldValue("mediaData", {
        mediaType: "image",
        fileName: "imagen.jpg",
        caption,
        media: base64Image,
      })
      : form.setFieldValue("message", editor.getWhatsAppContent());

    return notifications.show({
      title: "Mensaje confirmado",
      message: "Se ha establecido el contenido para la campaña",
      color: "green",
    });
  };

  useEffect(() => {
    const getEvolutionApiInstances = async () => {
      setEvolutionInstances((prev) => ({ ...prev, isLoading: true }));
      const URL = `${process.env.REACT_APP_EVOLUTION_API_URL}/instance/fetchInstances`;
      const API_KEY = process.env.REACT_APP_EVOLUTION_API_KEY;

      const options = {
        method: "GET",
        url: URL,
        headers: {
          apikey: API_KEY,
        },
      };

      axios(options)
        .then((response) => {
          const { data } = response;
          
          const responseData = data
            .filter((instance) => instance.connectionStatus === 'open')
            .map((instance) => {
            const { name } = instance;
            return name;
          });
          setEvolutionInstances((prev) => ({
            ...prev,
            data: responseData,
          }));
          form.setFieldValue("api_instances", responseData);
        })
        .catch((error) => {
          setEvolutionInstances(prev => ({
            ...prev,
            error: error.message
          }))
          showNotification({
            color: "red",
            status: "error",
            title: "Ha ocurrido un error",
            autoClose: 6000,
            message: `No se han cargado bien los bots. Por favor intente nuevamente. Detalle: ${error.message}`,
          });
        })
        .finally(() => {
          setEvolutionInstances((prev) => ({
            ...prev,
            isLoading: false,
          }));
        });
    };
    getEvolutionApiInstances();
  }, []);

  return (
    <section style={{ width: "100%" }}>
      <Stack spacing="xl">
        <Card
          withBorder
          shadow="sm"
          radius="md"
          style={{ backgroundColor: "var(--color-card-background)" }}
        >
          <Stack spacing="md">
            <Title
              order={2}
              style={{ color: "var(--color-text-primary)" }}
            >
              Envío de Mensajes
            </Title>

            <Divider my="sm" />

            <RichTextEditorComponent editor={editor} />

            <Button
              type="button"
              variant="outline"
              w="max-content"
              onClick={handleConfirmMessage}
            >
              {form.values?.message !== null ||
                form?.values?.mediaData !== null ? (
                <Flex align="center" gap={8}>
                  <IconCheck />
                  <span>Confirmado</span>
                </Flex>
              ) : (
                "Confirmar mensaje"
              )}
            </Button>
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md">
          <Stack spacing="md">
            <Title
              order={2}
              style={{ color: "var(--color-text-primary)" }}
            >
              Destinatarios
            </Title>
            <Text c="dimmed">
              Agrega los números de teléfono de los destinatarios
            </Text>

            <Divider my="sm" />

            <PhoneInput
              value={newPhone}
              onChange={(event) =>
                setNewPhone(event.target.value)
              }
              onAdd={handleAddPhone}
            />

            <FileInput
              label="O cargar desde CSV"
              placeholder="Seleccionar archivo"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              leftSection={<IconUpload size={14} />}
              mb="md"
            />

            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                backgroundColor: "var(--color-card-background)",
              }}
            >
              <Group justify="space-between" mb="md">
                <Text fw={500}>Números agregados</Text>
                <Badge size="lg" variant="light" color="blue">
                  {phoneNumbers.length}
                </Badge>
              </Group>

              <Box
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {phoneNumbersList}
              </Box>
            </Paper>
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md">
          <Accordion>
            <Accordion.Item value="configuracion">
              <Accordion.Control>
                <Title
                  order={2}
                  style={{
                    color: "var(--color-text-primary)",
                  }}
                >
                  Configuración avanzada
                </Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack spacing="md">
                  {evolutionInstances.isLoading && (
                    <Loader size="sm" />
                  )}
                  {evolutionInstances.error && (
                    <Stack gap={0}>
                      <Text
                        style={{
                          fontSize: "var(--fs-body)",
                        }}
                      >
                        Bots a utilizar:
                      </Text>
                      <Badge color="red">
                        Error:{" "}
                        {evolutionInstances.error}
                      </Badge>
                      <p
                        style={{
                          fontSize: "var(--fs-tiny)",
                          color: "var(--color-text-primary)",
                        }}
                      >
                        Por favor, racargue la página e
                        intente nuevamente
                      </p>
                    </Stack>
                  )}
                  {evolutionInstances?.data &&
                    evolutionInstances?.data?.length >
                    0 && (
                      <MultiSelect
                        data={evolutionInstances?.data}
                        label="Bots a utilizar"
                        description="A cada bot le tocará un número proporcional en relación a la cantidad de mensajes"
                        {...form.getInputProps(
                          "api_instances"
                        )}
                      />
                    )}

                  <NumberInput
                    label="Bloque de mensajes"
                    description="Cantidad de mensajes que se realizan por bloque antes de una pausa"
                    {...form.getInputProps("batch_size")}
                  />

                  <NumberInput
                    label="Tiempo de pausa entre bloques"
                    description="Expresado en minutos"
                    {...form.getInputProps("pause")}
                  />

                  <Grid>
                    <Grid.Col
                      span={12}
                      p={0}
                      mb={0}
                      mt={4}
                      pl={8}
                    >
                      <Title
                        order={6}
                        style={{
                          color: "var(--color-text-primary)",
                        }}
                      >
                        Intervalo de tiempo entre
                        mensajes
                      </Title>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Tooltip label="Ejemplo: 1 segundo = 1000 ms">
                        <NumberInput
                          label="Tiempo mínimo"
                          description="Expresado en milisegundos"
                          {...form.getInputProps(
                            "interval_range.min_ms"
                          )}
                        />
                      </Tooltip>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Tooltip label="Ejemplo 5 segundos = 5000 ms">
                        <NumberInput
                          label="Tiempo máximo"
                          description="Expresado en milisegundos"
                          {...form.getInputProps(
                            "interval_range.max_ms"
                          )}
                        />
                      </Tooltip>
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Card>
        <Button
          fullWidth
          type="submit"
          size="lg"
          loading={loading}
          disabled={
            evolutionInstances.isLoading ||
            evolutionInstances.error ||
            evolutionInstances?.data?.length === 0
          }
          leftSection={<IconSend size={20} />}
        >
          Enviar Mensajes
        </Button>
      </Stack>
    </section>
  );
}
